import type { OrgChatMessageDeliveryResult } from '@/contexts/org-chat-interface-context';
import type { DesignNavigationDelivery } from './navigation-delivery';
import {
  navigationDeliveryTargets,
  reconcileNavigationEntry,
} from './navigation-delivery-reconcile';

/**
 * `pending`: this app instance is sending it. `unconfirmed`: restored from
 * storage with an unknown outcome; hidden until the server confirms it.
 * `failed`: not accepted; the thread page offers a retry.
 */
export type NavigationDeliveryStatus = 'pending' | 'unconfirmed' | 'failed';
export type NavigationDeliveryEntry = {
  job: DesignNavigationDelivery;
  status: NavigationDeliveryStatus;
};
type Run = (
  job: DesignNavigationDelivery,
  checkpoint: (job: DesignNavigationDelivery) => void
) => Promise<OrgChatMessageDeliveryResult>;

export const navigationDeliveryKey = (job: DesignNavigationDelivery) =>
  JSON.stringify([job.actorId, job.orgSlug, job.id]);

/**
 * The most live status among /design sends to this route: a running send wins,
 * then one awaiting reconciliation, then a failure. `null` when none targets it.
 */
export function navigationDeliveryStatusForRoute(
  entries: readonly NavigationDeliveryEntry[],
  orgSlug: string,
  routeRef: string
): NavigationDeliveryStatus | null {
  let status: NavigationDeliveryStatus | null = null;
  for (const entry of entries) {
    if (entry.job.orgSlug !== orgSlug || entry.job.routeRef !== routeRef)
      continue;
    if (entry.status === 'pending') return 'pending';
    if (status === null || entry.status === 'unconfirmed')
      status = entry.status;
  }
  return status;
}

/**
 * A pending, unconfirmed, or failed /design send targets this route. The thread page keeps
 * its startup view (with the retry panel) instead of "thread not found" while
 * the first message is in flight or waiting for a retry.
 */
export function hasNavigationDeliveryForRoute(
  entries: readonly NavigationDeliveryEntry[],
  orgSlug: string,
  routeRef: string
): boolean {
  return navigationDeliveryStatusForRoute(entries, orgSlug, routeRef) !== null;
}

/** App-owned operations keep their receipt and retry targets when the source unmounts. */
export function createNavigationDeliveryStore(
  run: Run,
  accepted: (job: DesignNavigationDelivery) => void = () => {}
) {
  let entries: NavigationDeliveryEntry[] = [];
  const listeners = new Set<() => void>();
  const inFlight = new Map<string, Promise<OrgChatMessageDeliveryResult>>();
  const emit = () => {
    for (const listener of listeners) listener();
  };
  const put = (entry: NavigationDeliveryEntry) => {
    entries = [
      ...entries.filter(
        (item) =>
          navigationDeliveryKey(item.job) !== navigationDeliveryKey(entry.job)
      ),
      structuredClone(entry),
    ];
    emit();
  };
  const start = (
    job: DesignNavigationDelivery
  ): Promise<OrgChatMessageDeliveryResult> => {
    const key = navigationDeliveryKey(job);
    const running = inFlight.get(key);
    if (running) return running;
    const snapshot = structuredClone(job);
    put({ job: snapshot, status: 'pending' });
    const delivery = Promise.resolve()
      .then(() =>
        run(snapshot, (checkpoint) =>
          put({ job: checkpoint, status: 'pending' })
        )
      )
      .then(
        (receipt) => {
          if (receipt.accepted) {
            accepted(snapshot);
            entries = entries.filter(
              (item) => navigationDeliveryKey(item.job) !== key
            );
            emit();
          } else {
            put({
              job: {
                ...snapshot,
                options: receipt.retryOptions ?? snapshot.options,
              },
              status: 'failed',
            });
          }
          return receipt;
        },
        () => {
          put({ job: snapshot, status: 'failed' });
          return { status: 'started' as const, accepted: false };
        }
      )
      .finally(() => {
        inFlight.delete(key);
      });
    inFlight.set(key, delivery);
    return delivery;
  };
  return {
    start,
    getSnapshot: () => entries,
    subscribe: (listener: () => void) => {
      listeners.add(listener);
      return () => {
        listeners.delete(listener);
      };
    },
    /** A restored entry's outcome is unknown: never show it failed or replay it. */
    restore: (restored: NavigationDeliveryEntry[]) => {
      for (const entry of restored) {
        if (
          !entries.some(
            (item) =>
              navigationDeliveryKey(item.job) ===
              navigationDeliveryKey(entry.job)
          )
        )
          put({ job: entry.job, status: 'unconfirmed' });
      }
    },
    /** Apply the server's receipts for `checked` inputs to settled entries. */
    reconcile: (
      actorId: string,
      checked: readonly string[],
      acceptedInputIds: readonly string[]
    ) => {
      const checkedIds = new Set(checked);
      const acceptedIds = new Set(acceptedInputIds);
      const delivered: DesignNavigationDelivery[] = [];
      let changed = false;
      const next: NavigationDeliveryEntry[] = [];
      for (const entry of entries) {
        if (entry.status === 'pending' || entry.job.actorId !== actorId) {
          next.push(entry);
          continue;
        }
        const outcome = reconcileNavigationEntry(
          entry,
          checkedIds,
          acceptedIds
        );
        if (outcome.kind === 'unchanged') next.push(entry);
        else if (outcome.kind === 'accepted') delivered.push(entry.job);
        else next.push(outcome.entry);
        changed ||= outcome.kind !== 'unchanged';
      }
      if (!changed) return;
      entries = next;
      for (const job of delivered) accepted(job);
      emit();
    },
    /** The lookup failed: surface checked entries for an idempotent manual retry. */
    failUnconfirmed: (actorId: string, checked: readonly string[]) => {
      const checkedIds = new Set(checked);
      let changed = false;
      entries = entries.map((entry) => {
        if (
          entry.status !== 'unconfirmed' ||
          entry.job.actorId !== actorId ||
          !navigationDeliveryTargets(entry.job).some((target) =>
            checkedIds.has(target.clientInputId)
          )
        )
          return entry;
        changed = true;
        return { ...entry, status: 'failed' };
      });
      if (changed) emit();
    },
    retry: (id: string, actorId: string, orgSlug: string) => {
      const entry = entries.find(
        (item) =>
          item.job.id === id &&
          item.job.actorId === actorId &&
          item.job.orgSlug === orgSlug
      );
      if (!entry)
        return Promise.resolve({ status: 'started' as const, accepted: false });
      return start(entry.job);
    },
  };
}
